import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Star } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import type { Event } from '@/types/events';

type Testimonial = Database['public']['Tables']['brand_testimonials']['Row'];

interface EventTestimonialsInfoProps {
  event: Event;
  brandId: string;
}

export function EventTestimonialsInfo({ event, brandId }: EventTestimonialsInfoProps) {
  const { toast } = useToast();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [selectedTestimonialIds, setSelectedTestimonialIds] = useState<string[]>(event.testimonial_ids || []);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, [brandId]);

  useEffect(() => {
    setSelectedTestimonialIds(event.testimonial_ids || []);
  }, [event.testimonial_ids]);

  const fetchTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('brand_testimonials')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setTestimonials(data || []);
    } catch (err: unknown) {
      toast({ title: 'Error', description: 'Failed to load testimonials', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTestimonial = (testimonialId: string) => {
    setSelectedTestimonialIds((prev) =>
      prev.includes(testimonialId) ? prev.filter((id) => id !== testimonialId) : [...prev, testimonialId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('os_events')
        .update({ testimonial_ids: selectedTestimonialIds })
        .eq('id', event.id);
      if (error) throw error;
      toast({ title: 'Success', description: 'Testimonials updated successfully' });
    } catch (err: unknown) {
      toast({ title: 'Error', description: 'Failed to update testimonials', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Testimonials</CardTitle>
        <p className="text-sm text-muted-foreground">Select testimonials to display on this event&apos;s page</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {testimonials.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No testimonials available.</p>
            <p className="text-sm mt-2">Create testimonials first to associate them with events.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="flex items-start gap-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <Checkbox
                    checked={selectedTestimonialIds.includes(testimonial.id)}
                    onCheckedChange={() => handleToggleTestimonial(testimonial.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{testimonial.name}</p>
                        {testimonial.title && <p className="text-sm text-muted-foreground">{testimonial.title}</p>}
                        {testimonial.company && <p className="text-sm text-muted-foreground">{testimonial.company}</p>}
                      </div>
                      {testimonial.rating && (
                        <div className="flex items-center gap-1">
                          {Array.from({ length: testimonial.rating }).map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-sm line-clamp-2">{testimonial.testimonial}</p>
                    {!testimonial.is_published && (
                      <span className="inline-block px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Unpublished</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Testimonials'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
